<template>
  <el-collapse v-model="activeNames" class="timeline-collapse">
    <el-collapse-item name="1" class="timeline-collapse-item">
      <template slot="title">
        <div class="timeline-header">
          <div class="timeline-header-left">
            <i class="el-icon-time timeline-icon"></i>
            <span class="timeline-title">Historial de factura</span>
          </div>
          <span v-if="events.length" class="timeline-count">{{
            events.length
          }}</span>
        </div>
      </template>

      <div v-if="loading" class="timeline-loading">
        <i class="el-icon-loading"></i> Cargando...
      </div>

      <div v-else-if="events.length === 0" class="timeline-empty">
        Sin eventos registrados.
      </div>

      <div v-else class="timeline-list">
        <div
          v-for="(event, index) in events"
          :key="index"
          class="timeline-item"
          :class="`timeline-item--${event.type}`"
        >
          <div class="timeline-dot">
            <i :class="['timeline-dot-icon', iconFor(event.type)]"></i>
          </div>
          <div class="timeline-content">
            <div class="timeline-label">{{ event.label }}</div>
            <div class="timeline-at">{{ formatDate(event.at) }}</div>
            <div v-if="event.meta && event.meta.email" class="timeline-meta">
              Enviado a: {{ event.meta.email }}
            </div>
            <div v-if="event.meta && event.meta.amount" class="timeline-meta">
              {{ event.meta.amount
              }}<span v-if="event.meta.method"> · {{ event.meta.method }}</span>
            </div>
            <div
              v-if="event.meta && event.meta.retention"
              class="timeline-meta"
            >
              Retención: {{ event.meta.retention }}
            </div>
            <div
              v-if="event.meta && event.meta.tentative_date"
              class="timeline-meta"
            >
              Fecha tentativa: {{ event.meta.tentative_date }}
            </div>
            <button
              v-if="event.meta && event.meta.voucher"
              type="button"
              class="timeline-voucher-link"
              @click.stop="openVoucher(event.meta.voucher)"
            >
              <i class="el-icon-view"></i> Ver comprobante
            </button>
          </div>
        </div>
      </div>
    </el-collapse-item>

    <!-- Modal custom: preview del comprobante (sale por encima del InvoicePreview) -->
    <transition name="voucher-fade">
      <div
        v-if="voucherModalVisible"
        class="voucher-overlay"
        @click.self="closeVoucher"
      >
        <div class="voucher-modal">
          <div class="voucher-modal__header">
            <div class="voucher-modal__title">
              <i class="el-icon-document"></i>
              <span>Comprobante de pago</span>
            </div>
            <button
              type="button"
              class="voucher-modal__close"
              @click="closeVoucher"
              aria-label="Cerrar"
            >
              <i class="el-icon-close"></i>
            </button>
          </div>
          <div class="voucher-modal__body">
            <iframe
              v-if="voucherIsPdf && voucherUrl"
              :src="voucherUrl"
              class="voucher-modal__pdf"
              frameborder="0"
            ></iframe>
            <img
              v-else-if="voucherUrl"
              :src="voucherUrl"
              alt="Comprobante"
              class="voucher-modal__img"
            />
          </div>
          <div class="voucher-modal__footer">
            <a
              :href="voucherUrl"
              target="_blank"
              rel="noopener"
              class="voucher-modal__download"
            >
              <i class="el-icon-download"></i> Descargar / Abrir en nueva
              pestaña
            </a>
          </div>
        </div>
      </div>
    </transition>
  </el-collapse>
</template>

<script>
import InvoiceService from "../services/invoiceService";

export default {
  name: "InvoiceTimeline",
  props: {
    invoiceId: { type: Number, required: true }
  },
  data() {
    return {
      activeNames: [],
      loading: false,
      events: [],
      voucherModalVisible: false,
      voucherUrl: null
    };
  },
  computed: {
    voucherIsPdf() {
      if (!this.voucherUrl) return false;
      return this.voucherUrl
        .toLowerCase()
        .split("?")[0]
        .endsWith(".pdf");
    }
  },
  watch: {
    activeNames(val) {
      if (val.includes("1") && this.events.length === 0) {
        this.load();
      }
    }
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await InvoiceService.getTimeline(this.invoiceId);
        this.events = res.data.data || [];
      } catch {
        this.events = [];
      } finally {
        this.loading = false;
      }
    },
    iconFor(type) {
      const icons = {
        created: "el-icon-document",
        sent: "el-icon-s-promotion",
        reminder: "el-icon-bell",
        approved: "el-icon-check",
        payment_reported: "el-icon-money",
        received: "el-icon-message",
        payment: "el-icon-bank-card",
        paid: "el-icon-circle-check"
      };
      return icons[type] || "el-icon-more";
    },
    formatDate(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleDateString("es-EC", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    openVoucher(url) {
      this.voucherUrl = url;
      this.voucherModalVisible = true;
      // Mover el overlay al body para escapar del stacking context del InvoicePreview
      this.$nextTick(() => {
        const overlay = this.$el.querySelector(".voucher-overlay");
        if (overlay && overlay.parentNode !== document.body) {
          document.body.appendChild(overlay);
          this._voucherOverlayEl = overlay;
        }
        document.addEventListener("keydown", this._onVoucherEscape);
        document.body.style.overflow = "hidden";
      });
    },
    closeVoucher() {
      this.voucherModalVisible = false;
      document.removeEventListener("keydown", this._onVoucherEscape);
      document.body.style.overflow = "";
      // Devolver el nodo a su lugar original para que la transición/render de Vue funcione bien al reabrir
      this.$nextTick(() => {
        if (
          this._voucherOverlayEl &&
          this._voucherOverlayEl.parentNode === document.body
        ) {
          document.body.removeChild(this._voucherOverlayEl);
          this._voucherOverlayEl = null;
        }
      });
    },
    _onVoucherEscape(e) {
      if (e.key === "Escape") this.closeVoucher();
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this._onVoucherEscape);
    document.body.style.overflow = "";
    if (
      this._voucherOverlayEl &&
      this._voucherOverlayEl.parentNode === document.body
    ) {
      document.body.removeChild(this._voucherOverlayEl);
    }
  }
};
</script>

<style lang="scss" scoped>
.timeline-collapse {
  border: none;
  width: 100%;
}

.timeline-collapse-item {
  background: #fff;
  border-radius: 12px !important;
  border: 1px solid #e8ecf2;
  padding: 4px 8px;
  margin: 0;
  overflow: hidden;
  width: 100%;
}

// Remueve el border-bottom interno por defecto de el-collapse-item
.timeline-collapse-item ::v-deep .el-collapse-item__header,
.timeline-collapse-item ::v-deep .el-collapse-item__wrap {
  border-bottom: none !important;
}

.timeline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 8px;
}

.timeline-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.timeline-icon {
  font-size: 16px;
  color: #3fdbbf;
}

.timeline-title {
  font-size: 14px;
  font-weight: 600;
  color: #31355c;
}

.timeline-count {
  background: #eef1f7;
  color: #31355c;
  font-size: 11px;
  font-weight: 700;
  width: 24px;
  height: 24px;
  line-height: 24px;
  border-radius: 50%;
  text-align: center;
  flex-shrink: 0;
}

.timeline-loading,
.timeline-empty {
  padding: 16px;
  font-size: 13px;
  color: #aaa;
  text-align: center;
}

.timeline-list {
  padding: 8px 4px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 19px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e8ecf2;
  }
}

.timeline-item {
  display: flex;
  gap: 12px;
  padding: 10px 8px;
  border-radius: 8px;
  transition: background 0.15s;
  position: relative;

  &:hover {
    background: #f7f8fb;
  }
}

.timeline-dot {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: #fff;
  border: 2px solid #e8ecf2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  z-index: 1;
}

.timeline-dot-icon {
  font-size: 14px;
  color: #9ca3af;
  line-height: 1;
}

.timeline-item--paid .timeline-dot-icon,
.timeline-item--approved .timeline-dot-icon {
  color: #1d9e75;
}

.timeline-item--payment_reported .timeline-dot-icon,
.timeline-item--payment .timeline-dot-icon {
  color: #f5a623;
}

.timeline-item--sent .timeline-dot-icon,
.timeline-item--received .timeline-dot-icon {
  color: #3fdbbf;
}

.timeline-item--paid .timeline-dot,
.timeline-item--approved .timeline-dot {
  border-color: #3fdbbf;
  background: #f0fdf9;
}

.timeline-item--payment_reported .timeline-dot,
.timeline-item--payment .timeline-dot {
  border-color: #f5a623;
  background: #fffaf0;
}

.timeline-content {
  flex: 1;
  min-width: 0;
}

.timeline-label {
  font-size: 13px;
  font-weight: 600;
  color: #31355c;
  margin-bottom: 2px;
}

.timeline-at {
  font-size: 12px;
  color: #9ca3af;
}

.timeline-meta {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.timeline-voucher-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 0;
  font-size: 12px;
  color: #3fdbbf;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;

  i {
    font-size: 13px;
  }

  &:hover {
    text-decoration: underline;
  }
}
</style>

<style lang="scss">
/* Estilos globales (no scoped porque el overlay se mueve a document.body) */
.voucher-overlay {
  position: fixed;
  inset: 0;
  background: rgba(30, 32, 58, 0.6);
  z-index: 3500; /* por encima de InvoicePreview (2100) y SigningConfirmModal (3000) */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
}

.voucher-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.voucher-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e8ecf2;
  flex-shrink: 0;
}

.voucher-modal__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #31355c;

  i {
    color: #3fdbbf;
    font-size: 18px;
  }
}

.voucher-modal__close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  i {
    font-size: 18px;
  }

  &:hover {
    background: #f5f7fb;
    color: #31355c;
  }
}

.voucher-modal__body {
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: #f7f8fc;
  min-height: 300px;
}

.voucher-modal__img {
  max-width: 100%;
  max-height: 70vh;
  display: block;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.voucher-modal__pdf {
  width: 100%;
  height: 70vh;
  border: none;
  background: #fff;
  border-radius: 6px;
}

.voucher-modal__footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px;
  border-top: 1px solid #e8ecf2;
  flex-shrink: 0;
}

.voucher-modal__download {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #31355c;
  font-weight: 500;
  text-decoration: none;
  padding: 8px 14px;
  border: 1px solid #e8ecf2;
  border-radius: 8px;
  transition: all 0.15s;

  i {
    color: #3fdbbf;
    font-size: 15px;
  }

  &:hover {
    border-color: #3fdbbf;
    background: #f0fdf9;
  }
}

/* Transición */
.voucher-fade-enter-active,
.voucher-fade-leave-active {
  transition: opacity 0.2s ease;

  .voucher-modal {
    transition: transform 0.2s ease;
  }
}
.voucher-fade-enter,
.voucher-fade-leave-to {
  opacity: 0;

  .voucher-modal {
    transform: translateY(20px);
  }
}

@media (max-width: 768px) {
  .voucher-overlay {
    padding: 10px;
  }
  .voucher-modal {
    max-height: 95vh;
  }
  .voucher-modal__pdf {
    height: 60vh;
  }
}
</style>
